import React from "react"
import { MdArrowForward } from "react-icons/md"
import { Button, Link } from "gatsby-interface"
import { MdLaunch, MdWarning } from "react-icons/md"
import { GlobalErrorType } from "@modules/graphql/types"
import { useTracker } from "@modules/analytics"
import Form from "../../../form/components/Form"
import useSetPreviewTargets from "../hooks/useSetPreviewTargets"
import { SettingsDetailsRow } from "@modules/ui/components/SettingsDetailsRow"
import { useValidateUniqueSiteName } from "@modules/site/shared/hooks/useValidateUniqueSiteName"
import {
  InputConnectedField,
  InputFieldBlock,
  FormFieldBlock,
  useConnectedField,
} from "gatsby-interface"
import { useField } from "formik"
import { BranchesCombobox } from "@modules/ui/components/BranchesCombobox"
import { useBranchesForSiteQuery } from "@modules/site/shared/queries.generated"
import { siteSettings as siteSettingsText } from "@modules/locales/default.js"
import { useTriggerErrorAlert } from "@modules/ui/components/ErrorAlert"
import { useConnectedCDN } from "@modules/site/shared/hooks/useConnectedCDN"
import { CdnVendor } from "@modules/graphql/types"
import { FormattedMessage, commonMessageTags } from "@modules/locales"
import { useHostingDetailsForSiteQuery } from "@modules/cdn/gatsbyCloudCdn/queries.generated"

function SiteDetailsForm({
  productionBranch,
  siteId,
  setFormVisible,
  siteDirectory,
  siteDescription,
  repositoryUrl,
  siteName,
  sitePrefix,
}) {
  const { vendor: cdnVendor } = useConnectedCDN(siteId)
  const isHostedOnGatsby = cdnVendor === CdnVendor.CloudCdn
  const [errorInvalidField, setErrorInvalidField] = React.useState(null)
  const [setPreviewTargets, setPreviewTargetsLoading] = useSetPreviewTargets(
    siteId
  )
  const [setError, errorAlert] = useTriggerErrorAlert()
  const { trackButtonClicked } = useTracker()

  const { data: hostingDetails } = useHostingDetailsForSiteQuery({
    variables: { siteId },
    fetchPolicy: `cache-and-network`,
    skip: cdnVendor !== CdnVendor.CloudCdn,
  })

  const hasCustomDomain = hostingDetails?.hostingDetailsForSite.some(
    ({ domains }) => {
      return domains.some(({ cloudCdnDefault }) => !cloudCdnDefault)
    }
  )

  const [
    verifySiteNameUniqueness,
    { loading: loadingSiteNameUniqueness, error: serverError, validationError },
  ] = useValidateUniqueSiteName()

  const handleSubmit = values => {
    trackButtonClicked(`Save`, {
      siteId,
      uiSource: `Site Settings - Site Details`,
    })

    setError(null)
    setErrorInvalidField(null)

    return verifySiteNameUniqueness({ name: values.siteName, siteId }).then(
      isValid => {
        if (isValid) {
          return setPreviewTargets({
            publicName: values.siteName,
            branch: values.productionBranch,
            directoryPath: values.directoryPath,
            description: values.description,
            sitePrefix: values.sitePrefix,
            isHostedOnGatsby: isHostedOnGatsby,
            successMessage: (
              <React.Fragment>
                <strong>Site details</strong> updated
              </React.Fragment>
            ),
          })
            .then(() => setFormVisible(false))
            .catch(err => {
              if (err.message === GlobalErrorType.InvalidSitePrefix) {
                return setErrorInvalidField(GlobalErrorType.InvalidSitePrefix)
              }

              return setError(err)
            })
        }
      }
    )
  }

  const initialValues = {
    productionBranch: productionBranch || ``,
    directoryPath: siteDirectory || `/`,
    description: siteDescription || ``,
    siteName: siteName || ``,
    sitePrefix: sitePrefix || ``,
  }

  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={initialValues}
      validate={values => {
        const errors = {}

        if (!values.siteName) {
          errors.siteName = `Enter a site name`
        }

        return errors
      }}
    >
      <Form.FormElement applySpacing>
        <SiteNameField
          name={`siteName`}
          label="Name"
          layout="horizontal"
          error={validationError || serverError}
        />

        <SitePrefixField
          name={`sitePrefix`}
          label="Site prefix"
          layout="horizontal"
          error={
            errorInvalidField === GlobalErrorType.InvalidSitePrefix
              ? siteSettingsText.messages.wrongSitePrefix
              : null
          }
          hostedOnCloud={isHostedOnGatsby}
          hasCustomDomain={hasCustomDomain}
        />

        <SettingsDetailsRow
          label="Repository"
          data-cy="site-details-repository-row"
          inForm
        >
          <Link href={repositoryUrl} target="_blank">
            {repositoryUrl.split("//")[1]}
            <MdLaunch />
          </Link>
        </SettingsDetailsRow>

        <BranchField
          name="productionBranch"
          label="Production branch"
          layout="horizontal"
          siteId={siteId}
          uiSource="Site Settings - Site Details"
        />

        <InputConnectedField
          name={`directoryPath`}
          label="Base directory"
          layout="horizontal"
        />
        <InputConnectedField
          name={`description`}
          label="Description"
          layout="horizontal"
          data-cy="site-description"
        />

        {errorAlert}

        <Form.Actions>
          <Button
            variant="SECONDARY"
            tone="NEUTRAL"
            type={`reset`}
            onClick={() => {
              trackButtonClicked(`Cancel`, {
                siteId,
                uiSource: `Site Settings - Site Details`,
              })

              return setFormVisible()
            }}
          >
            Cancel
          </Button>
          <Button
            data-cy="save-site-details-button"
            type="submit"
            loading={setPreviewTargetsLoading || loadingSiteNameUniqueness}
            rightIcon={<MdArrowForward />}
          >
            Save
          </Button>
        </Form.Actions>
      </Form.FormElement>
    </Form>
  )
}

export default SiteDetailsForm

function SiteNameField({ name, error, ...rest }) {
  const [connectedProps] = useConnectedField(name)

  return (
    <InputFieldBlock
      {...connectedProps}
      error={connectedProps.error || error}
      {...rest}
      name={name}
    />
  )
}

function SitePrefixField({
  name,
  error,
  hostedOnCloud,
  hasCustomDomain,
  ...rest
}) {
  const [connectedProps, , , { setValue }] = useConnectedField(name)

  return (
    <InputFieldBlock
      hint={
        <div
          css={theme => ({
            display: `grid`,
            rowGap: theme.space[1],
          })}
        >
          {hasCustomDomain && (
            <span>
              <MdWarning
                css={theme => ({
                  verticalAlign: `middle`,
                  color: theme.colors.orange[60],
                })}
              />{" "}
              {siteSettingsText.messages.editingSitePrefixWillRequire}
            </span>
          )}
          {hostedOnCloud && (
            <span>
              <FormattedMessage
                message={
                  siteSettingsText.messages.yourDefaultHostedSiteWillBeAt
                }
                values={{
                  prefix: connectedProps.value,
                }}
                tags={commonMessageTags}
              />
            </span>
          )}
          <span>{siteSettingsText.messages.sitePrefixHint}</span>
        </div>
      }
      {...connectedProps}
      error={connectedProps.error || error}
      {...rest}
      name={name}
      onChange={e => {
        const nextValue = e.target.value
        const prefix = nextValue.replace(/[^a-z0-9]/g, ``).slice(0, 30)
        setValue(prefix)
      }}
    />
  )
}

function BranchField({ siteId, name, label, uiSource, layout }) {
  const { trackButtonClicked } = useTracker()
  const [{ value }, { error }, { setValue }] = useField(name)
  const [searchCriteria, setSearchCriteria] = React.useState("")
  const { data } = useBranchesForSiteQuery({
    variables: {
      siteId,
      searchText: searchCriteria,
    },
  })

  return (
    <FormFieldBlock id={name} label={label} error={error} layout={layout}>
      {controlProps => (
        <BranchesCombobox
          {...controlProps}
          branches={data?.branchesForSite?.branches || []}
          aria-label={label}
          inputId={controlProps.id}
          selectedBranch={value}
          searchCriteria={searchCriteria}
          onSearchCriteriaChange={setSearchCriteria}
          onBranchChange={branchName => {
            trackButtonClicked(`Branch list`, {
              siteId,
              uiSource,
            })

            setValue(branchName)
          }}
        />
      )}
    </FormFieldBlock>
  )
}
